import referenceLibrary from "@/data/reference-tracks.json";
import type { SpotifyAudioFeatures } from "./spotify-music-analysis";

const FEATURE_KEYS = [
  "artist_popularity",
  "avg_track_popularity",
  "avg_duration",
  "preview_ratio",
  "explicit_ratio",
  "release_recency",
  "artist_followers",
  "popularity_variance",
] as const;

type FeatureKey = (typeof FEATURE_KEYS)[number];

export type SimilarityFeatureVector = Record<FeatureKey, number>;

type ReferenceArtist = {
  artist: string;
  spotifyArtistId?: string;
  features: SimilarityFeatureVector;
};

type ReferenceLibrary = Record<string, ReferenceArtist[]>;

const library = referenceLibrary as ReferenceLibrary;

const MAX_DISTANCE = Math.sqrt(FEATURE_KEYS.length);

function clamp01(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

function normalizeFollowers(followers?: number): number {
  if (!followers || followers <= 0) {
    return 0;
  }
  return clamp01(Math.log10(followers + 1) / 7);
}

function computeReleaseRecency(releaseDate?: string): number {
  if (!releaseDate) {
    return 0.7; // 중간값 기본
  }
  const year = parseInt(releaseDate.slice(0, 4), 10);
  if (Number.isNaN(year)) {
    return 0.7;
  }
  const startYear = 1960;
  const currentYear = new Date().getFullYear();
  return clamp01((year - startYear) / (currentYear - startYear));
}

function deriveVarianceFromAudio(
  audio?: SpotifyAudioFeatures
): number | undefined {
  if (!audio) {
    return undefined;
  }
  const energy = audio.energy ?? 0.6;
  const danceability = audio.danceability ?? 0.6;
  const acousticness = audio.acousticness ?? 0.3;
  const variance =
    Math.abs(energy - danceability) * 0.4 +
    acousticness * 0.2 +
    audio.speechiness * 0.2;
  return clamp01(variance);
}

export interface UserSimilarityContext {
  trackPopularity?: number;
  artistPopularity?: number;
  durationMs?: number;
  previewUrl?: string | null;
  explicit?: boolean;
  releaseDate?: string;
  artistFollowers?: number;
  popularityVariance?: number;
}

export interface SimilarArtistMatch {
  artist: string;
  genre: string;
  similarity: number; // 0~1
  spotifyArtistId?: string;
  externalUrl?: string;
}

function buildUserVector(
  input: UserSimilarityContext,
  audioFeatures?: SpotifyAudioFeatures
): SimilarityFeatureVector {
  const artistPopularity = clamp01(
    (input.artistPopularity ?? input.trackPopularity ?? 60) / 100
  );
  const trackPopularity = clamp01((input.trackPopularity ?? 60) / 100);
  const duration = clamp01((input.durationMs ?? 210_000) / 300_000);
  const previewRatio = input.previewUrl ? 1 : 0;
  const explicitRatio = input.explicit ? 1 : 0;
  const releaseRecency = computeReleaseRecency(input.releaseDate);
  const followers = normalizeFollowers(input.artistFollowers);
  const varianceFromAudio = deriveVarianceFromAudio(audioFeatures);
  const popularityVariance = clamp01(
    input.popularityVariance ?? varianceFromAudio ?? 0.05
  );

  return {
    artist_popularity: artistPopularity,
    avg_track_popularity: trackPopularity,
    avg_duration: duration,
    preview_ratio: previewRatio,
    explicit_ratio: explicitRatio,
    release_recency: releaseRecency,
    artist_followers: followers,
    popularity_variance: popularityVariance,
  };
}

function vectorDistance(
  a: SimilarityFeatureVector,
  b: SimilarityFeatureVector
): number {
  let sum = 0;
  for (const key of FEATURE_KEYS) {
    const diff = (a[key] ?? 0) - (b[key] ?? 0);
    sum += diff * diff;
  }
  return Math.sqrt(sum);
}

function similarityScore(
  a: SimilarityFeatureVector,
  b: SimilarityFeatureVector
): number {
  const distance = vectorDistance(a, b);
  const normalized = clamp01(distance / MAX_DISTANCE);
  return 1 - normalized;
}

export function findSimilarArtists(
  input: UserSimilarityContext,
  options?: { limit?: number; audioFeatures?: SpotifyAudioFeatures }
): SimilarArtistMatch[] {
  const limit = options?.limit ?? 6;
  const userVector = buildUserVector(input, options?.audioFeatures);

  const matches: SimilarArtistMatch[] = [];

  Object.entries(library).forEach(([genre, artists]) => {
    artists.forEach(artist => {
      const score = similarityScore(userVector, artist.features);
      matches.push({
        artist: artist.artist,
        genre,
        similarity: score,
        spotifyArtistId: artist.spotifyArtistId,
        externalUrl: artist.spotifyArtistId
          ? `https://open.spotify.com/artist/${artist.spotifyArtistId}`
          : undefined,
      });
    });
  });

  matches.sort((a, b) => b.similarity - a.similarity);

  return matches.slice(0, limit);
}

